import useApi from '../../../hooks/useApi';
import { useState } from 'react';
import CSVUploader, { CSVUploadResult } from '../../CsvUploader';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface SuccessData {
  trafficSource: {
    _id: string;
    name: string;
  };
  campaign: {
    _id: string;
    entryDomain: string;
    name: string
  };
  offer: {
    name: string;
    offerLink: string;
  };
  campaignOffer: {
    affiliateUrl: string;
  };
}

interface BulkWizardUploadProps {
  onError?: (error: string) => void;
  onClearError?: () => void;
  onSuccess?: (data: SuccessData[]) => void;
}

const BulkWizardUpload = ({ onError, onClearError, onSuccess }: BulkWizardUploadProps) => {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingData, setPendingData] = useState<CSVUploadResult | null>(null);
  const { api } = useApi();

  const handleFileUpload = (data: CSVUploadResult) => {
    if (!data.json) return;
    if (onClearError) {
      onClearError();
    }
    setPendingData(data);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingData?.json) return;

    setConfirmOpen(false);
    setLoading(true);

    try {
      const response = await api.post('/api/offers/bulk-wizard', {
        csv: pendingData.json,
      });

      const responseData = response?.data?.data || [];
      setPendingData(null);
      setLoading(false);

      if (onClearError) {
        onClearError();
      }

      if (onSuccess) {
        onSuccess(responseData);
      }
    } catch (error: any) {
      setLoading(false);
      const errorMessage = error?.response?.data?.errorMessage || error?.message || 'An error occurred while uploading the file.';
      if (onError) {
        onError(errorMessage);
      }
      console.error(error);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingData(null);
  };

  return (
    <>
      <CSVUploader onChange={handleFileUpload} loading={loading} toJson />
      <Dialog open={confirmOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Upload
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to upload this file? This will create campaigns based on the CSV data.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancel} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkWizardUpload;
